import { Dispatch, ReactNode, SetStateAction } from 'react';
import { ButtonColor, Theme, ValueOf } from '@redsift/design-system';
import { usePopover } from './usePopover';

/**
 * Context props.
 */
export type HiddenPopoverState = {
  /** Whether the popover should be hidden or removed from the DOM. */
  hideInsteadOfClose?: boolean;
  /** Method to set the hideInsteadOfClose prop. */
  setHideInsteadOfClose?: Dispatch<SetStateAction<boolean>>;
};
export type PopoverState =
  | (ReturnType<typeof usePopover> & {
      /** Class name to append to the trigger. */
      readonly triggerClassName?: string;
    } & HiddenPopoverState)
  | null;

/**
 * Component variant.
 */
export const PopoverPlacement = {
  top: 'top',
  right: 'right',
  bottom: 'bottom',
  left: 'left',
  'top-start': 'top-start',
  'top-end': 'top-end',
  'right-start': 'right-start',
  'right-end': 'right-end',
  'bottom-start': 'bottom-start',
  'bottom-end': 'bottom-end',
  'left-start': 'left-start',
  'left-end': 'left-end',
} as const;
export type PopoverPlacement = ValueOf<typeof PopoverPlacement>;

/**
 * Component props.
 */
export interface PopoverProps {
  /** Popover content width. Can be either based on the trigger, the available space or define with a number of pixels. */
  width?: 'trigger-width' | 'available-width' | number;
  /** Popover content min width. Can be either based on the trigger, the available space or define with a number of pixels. */
  minWidth?: 'trigger-width' | 'available-width' | number;
  /** Popover content max width. Can be either based on the trigger, the available space or define with a number of pixels. */
  maxWidth?: 'trigger-width' | 'available-width' | number;
  /** Button color that will be forward to the trigger. */
  color?: ButtonColor;
  /** Children. Can only be PopoverTrigger and PopoverContent. */
  children: ReactNode;
  /**
   * Default open status.
   * Used for uncontrolled version.
   */
  defaultOpen?: boolean;
  /**
   * Default placement of the popover.
   * @default 'bottom'
   */
  placement?: PopoverPlacement;
  /**
   * Whether the popover is a modal or not.
   * @default false
   */
  isModal?: boolean;
  /**
   * Whether the component is opened or not.
   * Used for controlled version.
   */
  isOpen?: boolean;
  /**
   * Space between trigger and content (in pixels).
   * @default 2
   */
  offset?: number;
  /** Method to handle component change. */
  onOpen?: (open: boolean) => void;
  /** Allows other components to be treated as trigger and content. */
  overrideDisplayName?: {
    content?: string;
    trigger?: string;
  };
  /** Role to apply to the popover. */
  role?: 'dialog' | 'menu' | 'listbox';
  /** Theme. */
  theme?: Theme;
  /** Class name to append to the trigger. */
  triggerClassName?: string;
}

export type StyledPopoverProps = PopoverProps;
